import type { ActivityOf } from '@wyn/content';
import type { SessionView } from '@wyn/contracts';
import { type InputTrace, type WoodcuttingSim, woodcuttingSim } from '@wyn/game-core';
import { useRef } from 'react';
import { HoldButton, Stage } from './parts.tsx';
import { useRealtime } from './useRealtime.ts';
import { createWoodScene, H, W } from './woodcutting-draw.ts';

/** The Logging Camp: wind the axe back and let fly in the gold band. */
export function WoodcuttingGame({ session, activity, onDone }: { session: SessionView; activity: ActivityOf<'woodcutting'>; onDone: (trace: InputTrace) => void }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const scene = useRef<{ draw: ReturnType<typeof createWoodScene>; g: CanvasRenderingContext2D } | null>(null);
  const rt = useRealtime<WoodcuttingSim>({
    create: () => woodcuttingSim(activity, session.level, session.seed!),
    onFrame: (sim, dt) => {
      const c = canvas.current;
      if (!c) return;
      if (!scene.current) {
        const d = window.devicePixelRatio || 1;
        c.width = W * d;
        c.height = H * d;
        const g = c.getContext('2d')!;
        g.setTransform(d, 0, 0, d, 0, 0);
        scene.current = { draw: createWoodScene(activity), g };
      }
      scene.current.draw(scene.current.g, sim, dt);
    },
    onDone: (_sim, trace) => onDone(trace),
  });
  const sim = rt.sim;
  const cut = sim ? Math.round(Math.max(0, Math.min(1, 1 - sim.hp / activity.hp)) * 100) : 0;
  return (
    <Stage fx={rt.fx}>
      <p className="text-sm text-ink-dim">
        Hold <b>Swing</b>, the tree, or Space to wind the axe back along the arc. Let go while it sits in the <b>gold</b> band. The band moves after every chop; the red cap means you overbalance. Clean cuts in a row build a 🔥 streak.
      </p>
      <canvas ref={canvas} className="mx-auto block aspect-[6/5] w-full max-w-[360px] touch-none border border-rule" {...rt.holdProps()} aria-label="The tree and your axe" />
      <div className="flex justify-between font-mono text-sm text-ink-dim">
        <span>Chops {sim?.chops ?? 0}/{sim?.maxChops ?? 0}{sim && sim.streak > 1 ? ` · 🔥 ×${sim.streak}` : ''}</span>
        <span>Cut {cut}%</span>
      </div>
      <HoldButton held={rt.isHeld()} {...rt.holdProps()} disabled={!!sim?.result}>🪓 Swing</HoldButton>
    </Stage>
  );
}
